import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { useEffect, useRef } from "react";

// Android hardware back for chat layers (viewer → sheet → thread). While any
// layer is open the topmost one handles back; with none open the listener is
// removed, so Capacitor's default (history back / exit) applies again and the
// app's existing navigation stays untouched.
const stack: Array<{ current: () => void }> = [];
let handle: Promise<PluginListenerHandle | null> | null = null;

const isAndroid = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";

function sync(): void {
  if (!isAndroid()) return;
  if (stack.length > 0 && !handle) {
    handle = import("@capacitor/app")
      .then(({ App }) => App.addListener("backButton", () => stack[stack.length - 1]?.current()))
      .catch(() => null);
  } else if (stack.length === 0 && handle) {
    const h = handle;
    handle = null;
    void h.then((x) => x?.remove());
  }
}

/** While `active`, Android back calls `onBack` (the latest one) instead of navigating. */
export function useBackHandler(active: boolean, onBack: () => void): void {
  const ref = useRef(onBack);
  ref.current = onBack;
  useEffect(() => {
    if (!active) return;
    const entry = { current: () => ref.current() };
    stack.push(entry);
    sync();
    return () => {
      const i = stack.lastIndexOf(entry);
      if (i !== -1) stack.splice(i, 1);
      sync();
    };
  }, [active]);
}
